import Icon from "../../../components/base/Icon";
import { toShortDateWithoutTime } from "../../../core/Global";
import MigrationRow from "../models/MigrationRow";


const money = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 })


// One customer: who they are, which system they are on, and when it runs out.
export default function MigrationCard(props: { row: MigrationRow, onMove: () => void, onRollBack: () => void }) {

    const r = props.row

    // Under a week is urgent, under a month is coming. A free plan does not run out.
    const urgency = r.isInactive ? "urgent"
        : r.isFree && !r.isLegacy ? "none"
        : r.daysLeft <= 7 ? "urgent"
            : r.daysLeft <= 30 ? "soon"
                : "later"

    return (
        <div className={"migration-card" + (r.isLegacy ? " is-legacy" : " is-plan")}>

            <div className="migration-card-who">
                <div className="migration-card-avatar">
                    {(r.name ?? r.username ?? "?").substring(0, 1).toUpperCase()}
                </div>
                <div className="min-w-0">
                    <div className="migration-card-name">
                        {r.name}
                        <span className="migration-card-username">@{r.username}</span>
                    </div>
                    <div className="migration-card-meta">
                        {r.company && <span><Icon icon="building" size={11} />{r.company}</span>}
                        {r.email && <span><Icon icon="envelope" size={11} />{r.email}</span>}
                        {r.phone && <span><Icon icon="phone" size={11} />{r.phone}</span>}
                    </div>
                </div>
            </div>

            <div className="migration-card-plan">
                {
                    r.isLegacy ?
                        <span className="badge badge-light-warning fw-bold px-3 py-2">
                            <Icon icon="clock-rotate-left" type="solid" size={10} className="me-1" />
                            Old subscription
                        </span>
                        :
                        <span className="badge badge-light-primary fw-bold px-3 py-2">
                            <Icon icon="circle-check" type="solid" size={10} className="me-1" />
                            {r.planName}
                        </span>
                }
                <div className="migration-card-price">
                    {r.isFree ? "Free" : money.format(r.price)}
                    {!r.isFree && <span>/{r.billing === "annual" ? "year" : "month"}</span>}
                    {!r.isLegacy && r.customPrice && <span className="badge badge-light-warning ms-2">custom</span>}
                </div>
                <div className="migration-card-sub">
                    {r.users} user{r.users === 1 ? "" : "s"} at once
                    {r.status && r.status !== "Active" && <span className="text-warning fw-bold"> · {r.status}</span>}
                </div>
                {r.pendingChange && <div className="migration-card-sub text-primary">{r.pendingChange}</div>}
                {
                    r.isLegacy && (r.addons?.length ?? 0) > 0 &&
                    <div className="migration-card-addons">
                        {
                            r.addons!.map(a =>
                                <span key={a.name} className={"migration-addon is-" + a.state.toLowerCase()}
                                    title={`${a.state} · ${a.state === "Expired" ? "ended" : "until"} ${toShortDateWithoutTime(new Date(a.expiresAt))}`}>
                                    {a.name}{a.state !== "Active" && <em>{a.state.toLowerCase()}</em>}
                                </span>
                            )
                        }
                    </div>
                }
            </div>

            {/* How the account is used: its people, who is in now, and what is left in the wallet. */}
            <div className="migration-card-stats">
                <div title="Team members (technicians)">
                    <Icon icon="users" type="solid" size={11} />
                    <b>{r.teamMembers ?? 0}</b> user{r.teamMembers === 1 ? "" : "s"}
                    {(r.technicians ?? 0) > 0 && <span> · {r.technicians} tech{r.technicians === 1 ? "" : "s"}</span>}
                </div>
                <div title="Signed in right now" className={(r.signedInNow ?? 0) > r.users ? "text-danger" : ""}>
                    <Icon icon="circle" type="solid" size={8} className={(r.signedInNow ?? 0) > 0 ? "text-success" : "text-gray-400"} />
                    <b>{r.signedInNow ?? 0}</b> signed in now
                </div>
                <div title="Wallet balance" className={(r.walletBalance ?? 0) < 0 ? "text-danger" : ""}>
                    <Icon icon="wallet" type="solid" size={11} />
                    {r.walletBalance === undefined || r.walletBalance === null ? "No wallet" : <><b>{money.format(r.walletBalance)}</b> in wallet</>}
                </div>
            </div>

            <div className={"migration-card-when is-" + urgency}>
                {
                    urgency === "none" ?
                        <>
                            <div className="migration-card-days">—</div>
                            <div className="migration-card-date">Does not run out</div>
                        </>
                        : r.isInactive ?
                        <>
                            <div className="migration-card-days">{r.status}</div>
                            <div className="migration-card-date">
                                {r.daysLeft < 0 ? `Ended ${-r.daysLeft} day${r.daysLeft === -1 ? "" : "s"} ago · ` : "Ends "}
                                {toShortDateWithoutTime(new Date(r.endsAt))}
                            </div>
                        </>
                        :
                        <>
                            <div className="migration-card-days">
                                {r.daysLeft <= 0 ? "Today" : r.daysLeft + (r.daysLeft === 1 ? " day" : " days")}
                            </div>
                            <div className="migration-card-date">
                                {r.status === "Paused" ? "Pause ends " : r.isLegacy ? "Renews " : r.status === "Cancels" ? "Ends " : "Renews "}
                                {toShortDateWithoutTime(new Date(r.endsAt))}
                            </div>
                        </>
                }
            </div>

            <div className="migration-card-action">
                {
                    r.canMove ?
                        <button type="button" className="btn btn-sm btn-primary"
                            onClick={e => { e.preventDefault(); props.onMove(); }}>
                            <Icon icon={r.isInactive ? "rotate-right" : "arrow-right-arrow-left"} type="solid" size={11} className="me-2" />
                            {r.isInactive ? "Reactivate on a plan" : "Move to new plan"}
                        </button>
                        : r.canRollBack ?
                            <button type="button" className="btn btn-sm btn-light-danger"
                                onClick={e => { e.preventDefault(); props.onRollBack(); }}>
                                <Icon icon="clock-rotate-left" type="solid" size={11} className="me-2" />
                                Roll back
                            </button>
                            :
                            <span className="text-gray-400 fs-8">On a new plan</span>
                }
            </div>

        </div>
    )
}
